// Migration script to move JSON data to Supabase
// Run this once after setting up your Supabase project

import { supabase, TABLES, isSupabaseConfigured } from '../lib/supabase.js';

/**
 * Migration script to populate Supabase with existing JSON data
 */
export const migrateJSONToSupabase = async (jsonData) => {
  if (!isSupabaseConfigured()) {
    return { success: false, error: 'Supabase not configured. Please check your environment variables.' };
  }

  try {
    console.log('🚀 Starting migration to Supabase...');

    // Step 1: Initialize hotel settings
    console.log('📝 Creating hotel settings...');
    const { error: settingsError } = await supabase
      .from(TABLES.HOTEL_SETTINGS)
      .insert({
        last_updated: jsonData.lastUpdated || new Date().toISOString(),
        version: jsonData.version || '1.0.0'
      });

    if (settingsError && !settingsError.message.includes('duplicate')) {
      throw settingsError;
    }

    // Step 2: Create menu sections
    console.log('📋 Creating menu sections...');
    const menuSectionOrder = jsonData.menuSectionOrder || Object.keys(jsonData.menuItems || {});
    const menuSectionNames = jsonData.menuSectionNames || {};
    
    // Default section names for backward compatibility
    const defaultSectionNames = {
      starters: 'Starters',
      pubClassics: 'Pub Classics',
      curries: 'Curries', 
      fromTheSea: 'From the Sea',
      burgers: 'Burgers',
      desserts: 'Desserts',
      sides: 'Sides',
      drinks: 'Drinks'
    };

    const sectionsToInsert = menuSectionOrder.map((sectionKey, index) => ({
      key: sectionKey,
      name: menuSectionNames[sectionKey] || defaultSectionNames[sectionKey] || sectionKey,
      display_order: index
    }));

    // Clear existing sections first
    await supabase.from(TABLES.MENU_SECTIONS).delete().neq('id', '00000000-0000-0000-0000-000000000000');
    
    const { error: sectionsError } = await supabase
      .from(TABLES.MENU_SECTIONS)
      .insert(sectionsToInsert);

    if (sectionsError) throw sectionsError;

    // Step 3: Create menu items
    console.log('🍽️ Creating menu items...');
    const menuItems = jsonData.menuItems || {};
    const itemsToInsert = [];

    Object.entries(menuItems).forEach(([sectionKey, items]) => {
      items.forEach(item => {
        itemsToInsert.push({
          item_id: item.id,
          section_key: sectionKey,
          name: item.name,
          description: item.description,
          price: item.price,
          available: item.available
        });
      });
    });

    // Clear existing items first
    await supabase.from(TABLES.MENU_ITEMS).delete().neq('id', '00000000-0000-0000-0000-000000000000');

    if (itemsToInsert.length > 0) {
      const { error: itemsError } = await supabase
        .from(TABLES.MENU_ITEMS)
        .insert(itemsToInsert);

      if (itemsError) throw itemsError;
    }

    // Step 4: Create opening hours
    console.log('🕒 Creating opening hours...');
    const openingHours = jsonData.openingHours || {};
    const hoursToInsert = [];

    Object.entries(openingHours).forEach(([venueType, hours]) => {
      hours.forEach(hour => {
        hoursToInsert.push({
          venue_type: venueType,
          day_of_week: hour.day,
          hours: hour.hours
        });
      });
    });

    // Clear existing hours first
    await supabase.from(TABLES.OPENING_HOURS).delete().neq('id', '00000000-0000-0000-0000-000000000000');

    if (hoursToInsert.length > 0) {
      const { error: hoursError } = await supabase
        .from(TABLES.OPENING_HOURS)
        .insert(hoursToInsert);

      if (hoursError) throw hoursError;
    }

    // Step 5: Create events
    console.log('🎉 Creating events...');
    const events = jsonData.events || [];
    const eventsToInsert = events.map(event => ({
      event_id: event.id,
      title: event.title,
      day: event.day,
      time: event.time,
      description: event.description,
      active: event.active
    }));

    // Clear existing events first
    await supabase.from(TABLES.EVENTS).delete().neq('id', '00000000-0000-0000-0000-000000000000');

    if (eventsToInsert.length > 0) {
      const { error: eventsError } = await supabase
        .from(TABLES.EVENTS)
        .insert(eventsToInsert);

      if (eventsError) throw eventsError;
    }

    // Step 6: Create contact info
    console.log('📞 Creating contact information...');
    const contactInfo = jsonData.contactInfo || {};
    const socialMedia = jsonData.socialMedia || {};

    // Clear existing contact info first
    await supabase.from(TABLES.CONTACT_INFO).delete().neq('id', '00000000-0000-0000-0000-000000000000');

    const { error: contactError } = await supabase
      .from(TABLES.CONTACT_INFO)
      .insert({
        phone: contactInfo.phone,
        email: contactInfo.email,
        address_name: contactInfo.address?.name,
        address_street: contactInfo.address?.street,
        address_city: contactInfo.address?.city,
        address_postcode: contactInfo.address?.postcode,
        facebook_url: socialMedia.facebook,
        booking_url: socialMedia.booking
      });

    if (contactError) throw contactError;

    console.log('✅ Migration completed successfully!');
    console.log(`📊 Migrated:`);
    console.log(`   - ${sectionsToInsert.length} menu sections`);
    console.log(`   - ${itemsToInsert.length} menu items`);
    console.log(`   - ${hoursToInsert.length} opening hours`);
    console.log(`   - ${eventsToInsert.length} events`);
    console.log(`   - Contact information`);

    return { success: true };

  } catch (error) {
    console.error('❌ Migration failed:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Helper function to load JSON data and run migration
 * This can be called from the browser console or a React component
 */
export const runMigration = async () => {
  try {
    // In a real scenario, you would load your JSON data here
    // For now, we'll provide instructions for manual execution
    console.log('🔧 To run the migration:');
    console.log('1. Copy your JSON data');
    console.log('2. Call migrateJSONToSupabase(yourJsonData)');
    console.log('3. Or use the migration component we\'ll create');
    
    return { success: true, message: 'Ready for migration' };
  } catch (error) {
    console.error('Error preparing migration:', error);
    return { success: false, error: error.message };
  }
};

// SQL schema for reference - run this in your Supabase SQL editor
export const SCHEMA_SQL = `
-- Core hotel information
CREATE TABLE IF NOT EXISTS hotel_settings (
  id UUID PRIMARY KEY DEFAULT uuid_generate_v4(),
  last_updated TIMESTAMP WITH TIME ZONE DEFAULT NOW(),
  version TEXT DEFAULT '1.0.0'
);

-- Menu sections (for ordering and custom names)
CREATE TABLE IF NOT EXISTS menu_sections (
  id UUID PRIMARY KEY DEFAULT uuid_generate_v4(),
  key TEXT UNIQUE NOT NULL,
  name TEXT NOT NULL,
  display_order INTEGER NOT NULL,
  created_at TIMESTAMP WITH TIME ZONE DEFAULT NOW(),
  updated_at TIMESTAMP WITH TIME ZONE DEFAULT NOW()
);

-- Menu items
CREATE TABLE IF NOT EXISTS menu_items (
  id UUID PRIMARY KEY DEFAULT uuid_generate_v4(),
  item_id TEXT NOT NULL,
  section_key TEXT NOT NULL,
  name TEXT NOT NULL,
  description TEXT,
  price TEXT NOT NULL,
  available BOOLEAN DEFAULT true,
  created_at TIMESTAMP WITH TIME ZONE DEFAULT NOW(),
  updated_at TIMESTAMP WITH TIME ZONE DEFAULT NOW(),
  FOREIGN KEY (section_key) REFERENCES menu_sections(key) ON DELETE CASCADE
);

-- Opening hours
CREATE TABLE IF NOT EXISTS opening_hours (
  id UUID PRIMARY KEY DEFAULT uuid_generate_v4(),
  venue_type TEXT NOT NULL,
  day_of_week TEXT NOT NULL,
  hours TEXT NOT NULL,
  created_at TIMESTAMP WITH TIME ZONE DEFAULT NOW()
);

-- Events
CREATE TABLE IF NOT EXISTS events (
  id UUID PRIMARY KEY DEFAULT uuid_generate_v4(),
  event_id TEXT UNIQUE NOT NULL,
  title TEXT NOT NULL,
  day TEXT NOT NULL,
  time TEXT NOT NULL,
  description TEXT,
  active BOOLEAN DEFAULT true,
  created_at TIMESTAMP WITH TIME ZONE DEFAULT NOW(),
  updated_at TIMESTAMP WITH TIME ZONE DEFAULT NOW()
);

-- Contact information
CREATE TABLE IF NOT EXISTS contact_info (
  id UUID PRIMARY KEY DEFAULT uuid_generate_v4(),
  phone TEXT,
  email TEXT,
  address_name TEXT,
  address_street TEXT,
  address_city TEXT,
  address_postcode TEXT,
  facebook_url TEXT,
  booking_url TEXT,
  updated_at TIMESTAMP WITH TIME ZONE DEFAULT NOW()
);

-- Enable Row Level Security (RLS)
ALTER TABLE hotel_settings ENABLE ROW LEVEL SECURITY;
ALTER TABLE menu_sections ENABLE ROW LEVEL SECURITY;
ALTER TABLE menu_items ENABLE ROW LEVEL SECURITY;
ALTER TABLE opening_hours ENABLE ROW LEVEL SECURITY;
ALTER TABLE events ENABLE ROW LEVEL SECURITY;
ALTER TABLE contact_info ENABLE ROW LEVEL SECURITY;

-- Create policies for public read access
CREATE POLICY "Allow public read access" ON hotel_settings FOR SELECT USING (true);
CREATE POLICY "Allow public read access" ON menu_sections FOR SELECT USING (true);
CREATE POLICY "Allow public read access" ON menu_items FOR SELECT USING (true);
CREATE POLICY "Allow public read access" ON opening_hours FOR SELECT USING (true);
CREATE POLICY "Allow public read access" ON events FOR SELECT USING (true);
CREATE POLICY "Allow public read access" ON contact_info FOR SELECT USING (true);

-- Create policies for write access
-- For now, allow all writes for migration and admin operations
-- In production, you can restrict this further with authentication
CREATE POLICY "Allow all write access" ON hotel_settings FOR ALL USING (true);
CREATE POLICY "Allow all write access" ON menu_sections FOR ALL USING (true);
CREATE POLICY "Allow all write access" ON menu_items FOR ALL USING (true);
CREATE POLICY "Allow all write access" ON opening_hours FOR ALL USING (true);
CREATE POLICY "Allow all write access" ON events FOR ALL USING (true);
CREATE POLICY "Allow all write access" ON contact_info FOR ALL USING (true);
`;